/**
 * This component will be used for PDP to display the add to bag button and add to favorite button, showing an error message if anything goes wrong when adding a product to the bag.
 *
 * @module views/components/AddToBagFavorite
 * @memberof -Common
 */
import './AddToBagFavorite.scss';

import React from 'react';

import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';

import Tag from '@ulta/modules/Tag/Tag';

/**
 * Represents a AddToBagFavorite component
 *
 * @method
 * @param {AddToBagFavoriteProps} props - React properties passed from composition
 * @returns AddToBagFavorite
 */
export const AddToBagFavorite = function( props ){
  const {
    comingSoon,
    id,
    invokeMutation,
    addToBagAction,
    addToBagErrorLabel,
    commerceId,
    inBagLabel,
    incrementBagAccessibilityLabel,
    maxQuantity,
    productId,
    quantity,
    removeFromBagAction,
    skuId,
    successLabel,
    updateBagAction,
    bookmarkAction,
    bookmarked,
    removeBookmarkAction,
    state
  } = props;

  return (
    <div className='AddToBagFavorite'>
      <div className='AddToBagFavorite__Container'>
        <div className='AddToBagFavorite__AddToBag'>
          <AsyncComponent
            id={ id }
            invokeMutation={ invokeMutation }
            addToBagAction={ addToBagAction }
            addToBagErrorLabel={ addToBagErrorLabel }
            commerceId={ commerceId }
            default={ props.default }
            inBagLabel={ inBagLabel }
            incrementBagAccessibilityLabel={ incrementBagAccessibilityLabel }
            key='AddToBagButton'
            maxQuantity={ maxQuantity }
            moduleName='AddToBagButton'
            productId={ productId }
            quantity={ quantity }
            removeFromBagAction={ removeFromBagAction }
            skuId={ skuId }
            state={ state }
            successLabel={ successLabel }
            updateBagAction={ updateBagAction }
          />
        </div>
        <div
          className='AddToBagFavorite__Favorite'
        >
          <AsyncComponent
            id={ id }
            invokeMutation={ invokeMutation }
            bookmarkAction={ bookmarkAction }
            bookmarked={ bookmarked }
            commerceId={ commerceId }
            key='ProductBookmark'
            moduleName='ProductBookmark'
            productActionsProps={ props }
            productId={ productId }
            quantity={ quantity }
            removeBookmarkAction={ removeBookmarkAction }
            skuId={ skuId }
            state={ state }
          />
        </div>
      </div>

      { props.default?.errorLabel && (
        <div className='AddToBagFavorite__Error'
          role='alert'
        >
          <Tag
            variant='utility'
            style='error'
            icon='exclamationCircle'
            message={ props.default.errorLabel }
          />
        </div>
      ) }
      { comingSoon && comingSoon.comingSoonMessage && (
        <div className='AddToBagFavorite__comingSoon'>
          <Tag
            variant='utility'
            style='notification'
            message={ comingSoon.comingSoonMessage }
          />
        </div>
      ) }
    </div>
  );
};

/**
 * Property type definitions
 * @typedef AddToBagFavoriteProps
 * @type {object}
 * @property {object} addToBagAction - Sets label and graphql
 * @property {object} updateBagAction - Sets label and graphql
 * @property {object} removeFromBagAction - Sets label and graphql
 * @property {string} successLabel - Sets success Label
 * @property {string} addToBagErrorLabel - Sets error Label
 * @property {string} inBagLabel - Sets in bag Label
 * @property {string} maxQuantity - Sets max quantity
 * @property {boolean} bookmarked - True/False value for the bookmarked
 * @property {object} bookmarkAction - Label to be displayed
 * @property {object} removeBookmarkAction - Label to remove from bag
 * @property {string} commerceId - Id that is used to update the bag
 */
export const propTypes = {
  addToBagAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  updateBagAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  removeFromBagAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  successLabel: PropTypes.string,
  addToBagErrorLabel: PropTypes.string,
  inBagLabel: PropTypes.string,
  maxQuantity: PropTypes.number,
  bookmarked: PropTypes.bool,
  bookmarkAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  removeBookmarkAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  commerceId: PropTypes.string
};

AddToBagFavorite.propTypes = propTypes;

export default AddToBagFavorite;
